import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Pencil, Trash2, ExternalLink, BarChart2 } from "lucide-react";
import SEO from "@/components/SEO";
import {
  adminMe,
  setAdminToken,
  getAdminToken,
  adminListResources,
  adminCreateResource,
  adminUpdateResource,
  adminDeleteResource,
  adminLearningTabStats,
} from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";

const AUDIENCES = [
  { key: "clients", label: "Clients" },
  { key: "aspiring_therapists", label: "Aspiring Therapists" },
  { key: "healthcare_pros", label: "Healthcare Pros" },
];

const RESOURCE_TYPES = ["article", "video", "book", "course", "pdf", "podcast", "tool"];

const BLANK = {
  title: "",
  description: "",
  url: "",
  audiences: ["clients"],
  resource_type: "article",
  source: "",
  sort_order: 0,
};

export default function AdminLearningHub() {
  const nav = useNavigate();
  const [resources, setResources] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // id being edited, "new", or null
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    adminMe()
      .then((res) => { if (res?.expires_at) setAdminToken(getAdminToken(), res.expires_at); })
      .catch(() => { setAdminToken(""); nav("/admin/login", { replace: true }); });
  }, [nav]);

  useSessionRenewal({
    onExpired: () => { setAdminToken(""); nav("/admin/login", { replace: true }); },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([adminListResources(), adminLearningTabStats()]);
      setResources(Array.isArray(list) ? list : []);
      setStats(s);
    } catch (err) {
      console.warn("load failed", err);
      toast.error("Could not load Learning Hub.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing("new"); setForm(BLANK); };
  const startEdit = (r) => {
    setEditing(r.id);
    setForm({
      title: r.title || "",
      description: r.description || "",
      url: r.url || "",
      audiences: r.audiences?.length ? r.audiences : ["clients"],
      resource_type: r.resource_type || "article",
      source: r.source || "",
      sort_order: r.sort_order || 0,
    });
  };

  const toggleAudience = (a) => {
    setForm((f) => {
      const has = f.audiences.includes(a);
      const next = has ? f.audiences.filter((x) => x !== a) : [...f.audiences, a];
      return { ...f, audiences: next.length ? next : [a] };
    });
  };

  const save = async () => {
    if (!form.title.trim() || !form.url.trim()) {
      toast.error("Title and URL are required.");
      return;
    }
    if (!form.audiences.length) {
      toast.error("Pick at least one audience.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        url: form.url.trim(),
        audiences: form.audiences,
        resource_type: form.resource_type,
        source: form.source.trim() || null,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editing === "new") {
        await adminCreateResource(payload);
        toast.success("Resource added.");
      } else {
        await adminUpdateResource(editing, payload);
        toast.success("Resource updated.");
      }
      setEditing(null);
      setForm(BLANK);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    setBusy(true);
    try {
      await adminDeleteResource(id);
      toast.success("Deleted.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="admin-learning-page" className="bg-white min-h-screen">
      <SEO title="Learning Hub" path="/admin/learning-hub" noIndex />

      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="ash-eyebrow">Ashlan Admin</span>
            <h1 className="text-2xl md:text-3xl font-bold text-[#080808]">Learning Hub</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/learn" target="_blank" rel="noopener noreferrer" data-testid="open-public-hub" className="text-sm text-[#3A7A8C] hover:underline inline-flex items-center gap-1.5">
              <ExternalLink size={14} /> View public hub
            </Link>
            <Link to="/admin/blog" data-testid="back-to-blog" className="text-sm text-[#3A7A8C] hover:underline inline-flex items-center gap-1.5">
              <ArrowLeft size={14} /> Blog console
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Tab popularity */}
        {stats && (
          <div className="mb-10" data-testid="tab-popularity">
            <div className="flex items-center gap-3 mb-4">
              <BarChart2 size={16} className="text-[#3A7A8C]" />
              <h2 className="text-base font-semibold uppercase tracking-[0.18em] text-[#6B7280]">
                Tab popularity · {stats.total_views} total views
              </h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {AUDIENCES.map((a) => {
                const count = stats.tabs?.[a.key] || 0;
                const pct = stats.total_views ? Math.round((100 * count) / stats.total_views) : 0;
                return (
                  <div key={a.key} className="border border-[#e5e7eb] p-4" data-testid={`stat-${a.key}`}>
                    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#6B7280]">
                      {a.label}
                    </div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-2xl font-bold tabular-nums text-[#080808]">{count}</span>
                      <span className="text-xs text-[#6B7280]">views ({pct}%)</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-[#F5F5F5] overflow-hidden">
                      <div className="h-full bg-[#5FA8B4]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-[#080808]">Curated resources</h2>
          {!editing && (
            <button
              type="button"
              onClick={startNew}
              data-testid="add-resource-button"
              className="ash-btn-primary"
            >
              <Plus size={14} /> Add resource
            </button>
          )}
        </div>

        {editing && (
          <div className="border border-[#3A7A8C] bg-[#F0F7F9] p-6 md:p-8 mb-8" data-testid="resource-editor">
            <h3 className="text-lg font-bold text-[#080808] mb-5">
              {editing === "new" ? "New resource" : "Edit resource"}
            </h3>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className="ash-label" htmlFor="r-title">Title *</label>
                <input id="r-title" data-testid="r-title" className="ash-input"
                  value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. American Massage Therapy Association — Pain Research" />
              </div>
              <div className="md:col-span-2">
                <label className="ash-label" htmlFor="r-url">URL *</label>
                <input id="r-url" data-testid="r-url" className="ash-input"
                  value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://…  or  /blog/<slug>" />
              </div>
              <div className="md:col-span-2">
                <label className="ash-label" htmlFor="r-desc">Short description</label>
                <textarea id="r-desc" data-testid="r-desc" className="ash-input ash-textarea !min-h-[80px]"
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <label className="ash-label" htmlFor="r-type">Resource type</label>
                <select id="r-type" data-testid="r-type" className="ash-input"
                  value={form.resource_type} onChange={(e) => setForm({ ...form, resource_type: e.target.value })}>
                  {RESOURCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="ash-label" htmlFor="r-source">Source (optional)</label>
                <input id="r-source" data-testid="r-source" className="ash-input"
                  value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                  placeholder="e.g. AMTA, Harvard Health" />
              </div>
              <div className="md:col-span-2">
                <span className="ash-label block">Audiences *</span>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="audience-picker">
                  {AUDIENCES.map((a) => {
                    const active = form.audiences.includes(a.key);
                    return (
                      <button key={a.key} type="button"
                        onClick={() => toggleAudience(a.key)}
                        data-testid={`audience-${a.key}`}
                        className={`px-3 py-2 text-xs uppercase tracking-[0.15em] font-semibold border ${
                          active
                            ? "bg-[#3A7A8C] text-white border-[#3A7A8C]"
                            : "bg-white text-[#3C4048] border-[#e5e7eb] hover:bg-[#F5F5F5]"
                        }`}>
                        {a.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="ash-label" htmlFor="r-sort">Sort order</label>
                <input id="r-sort" type="number" data-testid="r-sort" className="ash-input"
                  value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
              </div>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button type="button" disabled={busy} onClick={save} data-testid="save-resource"
                className="ash-btn-primary disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" /> : "Save"}
              </button>
              <button type="button" onClick={() => { setEditing(null); setForm(BLANK); }} className="ash-link-underline">
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12 text-[#5A5F6A]">
            <Loader2 className="animate-spin mr-2" /> Loading…
          </div>
        ) : resources.length === 0 ? (
          <div className="border border-dashed border-[#e5e7eb] p-12 text-center text-[#5A5F6A]" data-testid="empty-resources">
            No curated resources yet. Click <strong>Add resource</strong> to seed your first one.
          </div>
        ) : (
          <div className="border border-[#e5e7eb] divide-y divide-[#e5e7eb]" data-testid="resources-list">
            {resources.map((r) => (
              <div key={r.id} className="p-5 flex flex-wrap items-start gap-4" data-testid={`resource-row-${r.id}`}>
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#3A7A8C]">
                      {r.resource_type}
                    </span>
                    {r.source && (
                      <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">
                        · {r.source}
                      </span>
                    )}
                    <div className="flex gap-1 ml-2">
                      {r.audiences?.map((a) => (
                        <span key={a} className="text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#F5F5F5] text-[#3C4048] px-1.5 py-0.5">
                          {a.replace("_", " ")}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="font-semibold text-[#080808]">{r.title}</div>
                  {r.description && (
                    <p className="mt-1 text-sm text-[#3C4048] line-clamp-2">{r.description}</p>
                  )}
                  <a href={r.url} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-[#3A7A8C] hover:underline mt-1 inline-flex items-center gap-1">
                    {r.url} <ExternalLink size={10} />
                  </a>
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => startEdit(r)} data-testid={`edit-${r.id}`}
                    className="p-2 hover:bg-[#F5F5F5] text-[#3A7A8C]" aria-label="Edit">
                    <Pencil size={14} />
                  </button>
                  <button type="button" onClick={() => remove(r.id, r.title)} data-testid={`delete-${r.id}`}
                    className="p-2 hover:bg-[#FEE2E2] text-[#991B1B]" aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
